import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { CreditProvider } from '@/contexts/CreditContext';
import { ModuleProvider, useIsModuleEnabled } from '@/modules/ModuleContext';
import { AppLayout, AuthLayout } from '@/components/layout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import theme from '@/theme';

// Eagerly loaded pages (critical path - login/register)
import { Login, Register, ForgotPassword, ResetPassword } from './pages';

// Lazily loaded public pages (SEO-optimized)
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const Features = lazy(() => import('./pages/Features').then(m => ({ default: m.Features })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({ default: m.FAQ })));

// Lazily loaded pages (code splitting for better initial load)
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const SurveyList = lazy(() => import('./pages/SurveyList').then(m => ({ default: m.SurveyList })));
const SurveyBuilder = lazy(() => import('./pages/SurveyBuilder').then(m => ({ default: m.SurveyBuilder })));
const PublicSurvey = lazy(() => import('./pages/PublicSurvey').then(m => ({ default: m.PublicSurvey })));
const ResponseList = lazy(() => import('./pages/ResponseList').then(m => ({ default: m.ResponseList })));
const ResponseDetail = lazy(() => import('./pages/ResponseDetail').then(m => ({ default: m.ResponseDetail })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));

// Exam pages - lazily loaded
const ExamList = lazy(() => import('./pages/ExamList').then(m => ({ default: m.ExamList })));
const ExamBuilder = lazy(() => import('./pages/ExamBuilder').then(m => ({ default: m.ExamBuilderMain })));
const ExamTaking = lazy(() => import('./pages/ExamTaking').then(m => ({ default: m.ExamTaking })));
const ExamResults = lazy(() => import('./pages/ExamResults').then(m => ({ default: m.ExamResults })));
const ExamGrading = lazy(() => import('./pages/ExamGrading').then(m => ({ default: m.ExamGrading })));
const ExamAttempts = lazy(() => import('./pages/ExamAttempts').then(m => ({ default: m.ExamAttempts })));
const PublicExam = lazy(() => import('./pages/PublicExam').then(m => ({ default: m.PublicExam })));
const PublicExamResults = lazy(() => import('./pages/PublicExamResults').then(m => ({ default: m.PublicExamResults })));

// Quiz pages - REMOVED (module disabled)
// const QuizList = lazy(() => import('./pages/QuizList').then(m => ({ default: m.QuizList })));
// const QuizBuilder = lazy(() => import('./pages/QuizBuilder').then(m => ({ default: m.QuizBuilderMain })));
// const QuizTaking = lazy(() => import('./pages/QuizTaking').then(m => ({ default: m.QuizTaking })));
// const QuizResults = lazy(() => import('./pages/QuizResults').then(m => ({ default: m.QuizResults })));

// Assessment pages - REMOVED (module disabled)
// const AssessmentList = lazy(() => import('./pages/AssessmentList').then(m => ({ default: m.AssessmentList })));
// const AssessmentBuilder = lazy(() => import('./pages/AssessmentBuilder').then(m => ({ default: m.AssessmentBuilderMain })));
// const AssessmentSubmission = lazy(() => import('./pages/AssessmentSubmission').then(m => ({ default: m.AssessmentSubmission })));
// const PeerReviewPage = lazy(() => import('./pages/PeerReviewPage').then(m => ({ default: m.PeerReviewPage })));
// const AssessmentResults = lazy(() => import('./pages/AssessmentResults').then(m => ({ default: m.AssessmentResults })));

// Admin pages - lazily loaded
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const UserManagement = lazy(() => import('./pages/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const QuestionTypesManager = lazy(() => import('./pages/admin/QuestionTypesManager').then(m => ({ default: m.QuestionTypesManager })));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })));

// Admin Billing pages - lazily loaded
const AdminBillingDashboard = lazy(() => import('./pages/admin/billing/BillingDashboard'));
const SubscriptionPlansManager = lazy(() => import('./pages/admin/billing/SubscriptionPlansManager'));
const CreditPacksManager = lazy(() => import('./pages/admin/billing/CreditPacksManager'));
const InvoicesManager = lazy(() => import('./pages/admin/billing/InvoicesManager'));
const BillingSettings = lazy(() => import('./pages/admin/billing/BillingSettings'));

// Billing pages - lazily loaded
const BillingDashboard = lazy(() => import('./pages/Billing/BillingDashboard').then(m => ({ default: m.BillingDashboard })));
const PricingPage = lazy(() => import('./pages/Billing/PricingPage').then(m => ({ default: m.PricingPage })));
const CreditPurchasePage = lazy(() => import('./pages/Billing/CreditPurchasePage').then(m => ({ default: m.CreditPurchasePage })));
const TransactionHistoryPage = lazy(() => import('./pages/Billing/TransactionHistoryPage').then(m => ({ default: m.TransactionHistoryPage })));
const InvoicesPage = lazy(() => import('./pages/Billing/InvoicesPage').then(m => ({ default: m.InvoicesPage })));

// Loading spinner component
function LoadingScreen() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );
}

// Protected route wrapper - redirects to login if not authenticated
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

// Public route wrapper - redirects to dashboard if already authenticated
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <AuthLayout>{children}</AuthLayout>;
}

// Admin route wrapper - requires admin role
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You don't have permission to access this page. Admin privileges required.
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return <AppLayout>{children}</AppLayout>;
}

// Suspense fallback for lazy-loaded pages
function PageLoadingFallback() {
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );
}

// Main routes component - must be inside AuthProvider
function AppRoutes() {
  const isExamEnabled = useIsModuleEnabled('exam');

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
      {/* Public auth routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public marketing pages (no auth required, SEO-optimized) */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/features" element={<Features />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />

      {/* Public pages (no auth required) */}
      <Route path="/s/:token" element={<PublicSurvey />} />
      <Route path="/e/:token" element={<PublicExam />} />
      <Route path="/e/:token/results/:attemptId" element={<PublicExamResults />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Exam routes (requires auth) - Only render if exam module is enabled */}
      {isExamEnabled && (
        <>
          <Route
            path="/exams"
            element={
              <ProtectedRoute>
                <ExamList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/new"
            element={
              <ProtectedRoute>
                <ExamBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/:id/edit"
            element={
              <ProtectedRoute>
                <ExamBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/:examId/take"
            element={<ExamTaking />}
          />
          <Route
            path="/exams/:examId/results/:attemptId"
            element={
              <ProtectedRoute>
                <ExamResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/:examId/attempts"
            element={
              <ProtectedRoute>
                <ExamAttempts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/attempts/:attemptId/grade"
            element={
              <ProtectedRoute>
                <ExamGrading />
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* Quiz routes - REMOVED (module disabled) */}
      {/* <Route
        path="/quizzes"
        element={
          <ProtectedRoute>
            <QuizList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quizzes/new"
        element={
          <ProtectedRoute>
            <QuizBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quizzes/:id/edit"
        element={
          <ProtectedRoute>
            <QuizBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quizzes/:quizId/take"
        element={
          <ProtectedRoute>
            <QuizTaking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quizzes/:quizId/results/:attemptId"
        element={
          <ProtectedRoute>
            <QuizResults />
          </ProtectedRoute>
        }
      /> */}

      {/* Assessment routes - REMOVED (module disabled) */}
      {/* <Route
        path="/assessments"
        element={
          <ProtectedRoute>
            <AssessmentList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/new"
        element={
          <ProtectedRoute>
            <AssessmentBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/:id/edit"
        element={
          <ProtectedRoute>
            <AssessmentBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/:assessmentId/submit"
        element={
          <ProtectedRoute>
            <AssessmentSubmission />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/:assessmentId/review/:reviewId"
        element={
          <ProtectedRoute>
            <PeerReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/:assessmentId/results"
        element={
          <ProtectedRoute>
            <AssessmentResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/:assessmentId/results/:submissionId"
        element={
          <ProtectedRoute>
            <AssessmentResults />
          </ProtectedRoute>
        }
      /> */}

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      {/* Survey routes */}
      <Route
        path="/surveys"
        element={
          <ProtectedRoute>
            <SurveyList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/:id/edit"
        element={
          <ProtectedRoute>
            <SurveyBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/new"
        element={
          <ProtectedRoute>
            <SurveyBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/:id/responses"
        element={
          <ProtectedRoute>
            <ResponseList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/responses/:id"
        element={
          <ProtectedRoute>
            <ResponseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/:id/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />

      {/* Admin routes with sidebar layout */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        {/* Admin Dashboard */}
        <Route index element={<AdminDashboard />} />

        {/* Admin Management */}
        <Route path="users" element={<UserManagement />} />
        <Route path="question-types" element={<QuestionTypesManager />} />
        <Route path="settings" element={<AdminSettings />} />

        {/* Admin Billing Routes */}
        <Route path="billing" element={<AdminBillingDashboard />} />
        <Route path="billing/plans" element={<SubscriptionPlansManager />} />
        <Route path="billing/credit-packs" element={<CreditPacksManager />} />
        <Route path="billing/invoices" element={<InvoicesManager />} />
        <Route path="billing/settings" element={<BillingSettings />} />
      </Route>

      {/* Billing routes */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/credits"
        element={
          <ProtectedRoute>
            <CreditPurchasePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/history"
        element={
          <ProtectedRoute>
            <TransactionHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/invoices"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute>
        }
      />

        {/* Fallback for unmatched routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <CreditProvider>
                  <ModuleProvider apiEndpoint="/api/configuration/modules">
                    <AppRoutes />
                  </ModuleProvider>
                </CreditProvider>
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
